import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Breadcrumb from "../../DefaultComponent/BreadCrumb";
import Spinner from "../../DefaultComponent/Spinner";
import { formatDate, calculateTotalPages } from "../../../utils/Helper";
import { url, MARKETING_SITE_URL } from "../../../Config/Config";
import { CmsPageListAction, CmsPagePublishApi, CmsPageArchiveApi } from "../../../Redux/Actions/CmsAction";
import type { ContentPageListItem } from "../../../types/cms";
import CmsStatusBadge from "../shared/CmsStatusBadge";
import CmsApiErrorAlert from "../shared/CmsApiErrorAlert";
import CmsConfirmDialog from "../shared/CmsConfirmDialog";

type StatusFilter = "all" | "draft" | "published";

const CmsPageListPage: React.FC = () => {
  const dispatch = useDispatch<any>();
  const listState = useSelector((state: any) => state.CmsPageListData);
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [ordering, setOrdering] = useState<"newest" | "oldest">("newest");
  const [loading, setLoading] = useState(false);

  const [publishSlug, setPublishSlug] = useState<string | null>(null);
  const [archiveSlug, setArchiveSlug] = useState<string | null>(null);

  const fetchList = useCallback(() => {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("page_size", String(rowsPerPage));
    if (statusFilter !== "all") {
      params.set("status", statusFilter);
    }
    if (includeArchived) {
      params.set("include_archived", "true");
    }
    if (search.trim()) {
      params.set("search", search.trim());
    }
    params.set("ordering", ordering);
    setLoading(true);
    dispatch(CmsPageListAction(params.toString())).finally(() =>
      setLoading(false)
    );
  }, [dispatch, page, rowsPerPage, statusFilter, includeArchived, search, ordering]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput), 400);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const rows: ContentPageListItem[] = listState?.data?.row ?? [];
  const totalCount: number = listState?.data?.total_count ?? 0;
  const totalPages = calculateTotalPages(totalCount, rowsPerPage);

  const listError =
    listState?.loading === false &&
    listState?.status === false &&
    listState?.data;

  const errorMessage =
    typeof listError === "object" && listError !== null
      ? (listError as { message?: string }).message
      : undefined;
  const errorList =
    typeof listError === "object" && listError !== null
      ? (listError as { errors?: string[] }).errors
      : undefined;

  const handlePublish = async () => {
    if (!publishSlug) return;
    const res = await CmsPagePublishApi(publishSlug, true);
    if (res && res.status === true) {
      fetchList();
    }
    setPublishSlug(null);
  };

  const handleArchive = async () => {
    if (!archiveSlug) return;
    const res = await CmsPageArchiveApi(archiveSlug, true);
    if (res && res.status === true) {
      fetchList();
    }
    setArchiveSlug(null);
  };

  const publicBase = MARKETING_SITE_URL;

  return (
    <>
      {(loading || listState?.loading) && <Spinner />}
      <div className="page-wrapper card">
        <div className="col-md-12">
          <Breadcrumb
            pageTitle="Content pages"
            breadcrumbs={[
              { label: "Content pages", path: `${url}/cms/pages`, isActive: true },
            ]}
          />
        </div>

        <Box sx={{ m: 2 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ md: "center" }}
            justifyContent="space-between"
          >
            <Typography variant="h5">CMS — public pages</Typography>
            <Button
              variant="contained"
              onClick={() => navigate(`${url}/cms/pages/new`)}
            >
              Create page
            </Button>
          </Stack>

          <CmsApiErrorAlert message={errorMessage} errors={errorList} />

          <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mt: 2 }}>
            <TextField
              select
              label="Status"
              size="small"
              value={statusFilter}
              onChange={(e) => {
                setPage(1);
                setStatusFilter(e.target.value as StatusFilter);
              }}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="draft">Drafts</MenuItem>
              <MenuItem value="published">Published</MenuItem>
            </TextField>
            <TextField
              select
              label="Sort"
              size="small"
              value={ordering}
              onChange={(e) => {
                setPage(1);
                setOrdering(e.target.value as "newest" | "oldest");
              }}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="newest">Newest</MenuItem>
              <MenuItem value="oldest">Oldest</MenuItem>
            </TextField>
            <TextField
              label="Search title or slug"
              size="small"
              value={searchInput}
              onChange={(e) => {
                setPage(1);
                setSearchInput(e.target.value);
              }}
              sx={{ flex: 1, minWidth: 200 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeArchived}
                  onChange={(_, c) => {
                    setPage(1);
                    setIncludeArchived(c);
                  }}
                />
              }
              label="Show archived"
            />
          </Stack>

          <TableContainer component={Paper} sx={{ mt: 2, minWidth: 720 }}>
            <Table size="small">
              <TableHead sx={{ backgroundColor: "#0555c8" }}>
                <TableRow>
                  <TableCell sx={{ color: "white" }}>#</TableCell>
                  <TableCell sx={{ color: "white" }}>Title</TableCell>
                  <TableCell sx={{ color: "white" }}>Slug</TableCell>
                  <TableCell sx={{ color: "white" }}>Status</TableCell>
                  <TableCell sx={{ color: "white" }}>Updated</TableCell>
                  <TableCell sx={{ color: "white" }} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography sx={{ py: 3 }} align="center" color="text.secondary">
                        No pages match your filters. Create a page to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row, idx) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{(page - 1) * rowsPerPage + idx + 1}</TableCell>
                      <TableCell>{row.title}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                          {row.slug}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <CmsStatusBadge status={row.status} markDel={row.mark_del} />
                      </TableCell>
                      <TableCell>{formatDate(row.updated_at)}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              navigate(`${url}/cms/pages/${row.slug}/edit`)
                            }
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="info"
                            onClick={() =>
                              navigate(`${url}/cms/pages/${row.slug}/edit?tab=preview`)
                            }
                          >
                            Preview
                          </Button>
                          {row.status === "draft" && !row.mark_del ? (
                            <Button
                              size="small"
                              color="success"
                              variant="outlined"
                              onClick={() => setPublishSlug(row.slug)}
                            >
                              Publish
                            </Button>
                          ) : null}
                          {!row.mark_del ? (
                            <Button
                              size="small"
                              color="error"
                              variant="outlined"
                              onClick={() => setArchiveSlug(row.slug)}
                            >
                              Archive
                            </Button>
                          ) : null}
                          {row.status === "published" && !row.mark_del && publicBase ? (
                            <Button
                              size="small"
                              href={`${publicBase}/${row.slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              View public
                            </Button>
                          ) : null}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, p) => setPage(p)}
                color="primary"
              />
            </Box>
          ) : null}
        </Box>
      </div>

      <CmsConfirmDialog
        open={!!publishSlug}
        title="Publish page?"
        description="Published pages are visible on the public site (when not archived)."
        confirmLabel="Publish"
        onClose={() => setPublishSlug(null)}
        onConfirm={handlePublish}
      />
      <CmsConfirmDialog
        open={!!archiveSlug}
        title="Archive page?"
        description="Archived pages return 404 on the public API and can no longer be edited."
        confirmLabel="Archive"
        danger
        onClose={() => setArchiveSlug(null)}
        onConfirm={handleArchive}
      />
    </>
  );
};

export default CmsPageListPage;
